import { Injectable, Provider } from "@angular/core";
import { ToastItem } from "projects/shared/lib/components/shared/toast/item/toast-item.model";
import { EToastItemType } from "projects/shared/lib/enum/toast/toast-entry-type.enum";
import { CartService } from "projects/shared/lib/services/data/cart/cart.service";
import { ServiceLocator } from "projects/shared/lib/services/infrastructure/service-locator.service";
import {
  EShoppingCartGetQueryType, EShoppingCartItemValidationError, EShoppingCartValidationError, ProductIdentifierDto, ProductStockInformationDto, ProductStockInformationGetDto,
  ShoppingCartDto, ShoppingCartGetQueryResult, ShoppingCartSetItemDto, ShoppingCartValidationException, ShoppingCartValidationResultDto, SwaggerException
} from "projects/shared/lib/services/INTEGRA_Shop_Client";
import { CartValidationHelperService } from "projects/shared/lib/services/ui/cart-validation-helper.service";
import { firstValueFrom, Subscription } from "rxjs";
import { ENVIRONMENT } from "projects/shared/lib/environment/environment";
import { ICartCreationDialogService } from 'shared/services/ui/cart-creation-dialog/cart-creation-dialog-service.abstract';
import { ICartServiceToken } from "./cart-service.interface";
import { IToastServiceToken } from "shared/services/ui/toast/toast-service.interface";


@Injectable({
  providedIn: 'root'
})
export class CartTourService extends CartService {
  private permissionSubscription: Subscription | undefined;
  private _cartCreationDialogService: ICartCreationDialogService | null = null;
  get cartCreationDialogService(): ICartCreationDialogService {
    if (!this._cartCreationDialogService) {
      this._cartCreationDialogService = ServiceLocator.injector.get(ICartCreationDialogService);
    }
    return this._cartCreationDialogService;
  }
  private isStockError: boolean = false;

  public constructor() {
    super();
    this.cartSubscriptionInit();
    this.customerNumberSubscriptionInit();
    this.userPermissionSubscriptionInit();
    this.permissionSubscriptionInit();
    this.authenticationSubscriptionInit();
  }

  private userPermissionSubscriptionInit() {
    if (!this.permissionSubscription) {
      this.permissionSubscription = this.authService.userPermissionChangedGet().subscribe(
        () => {
          this.shouldLoadPrices = this.shouldLoadPricesCurrentStateGet();
        }
      );
    }
  }

  private customerNumberSubscriptionInit() {
    this.authService.customerNumberChangedGet().subscribe(
      () => {
        this.isLoggedIn = this.authService.isAuthenticated();
        this.shouldLoadPrices = this.shouldLoadPricesCurrentStateGet();
        this.update();
      }
    );
  }

  public async cartLatestGetMergedWithCartCurrentAsync(
    updateLatestCartViaApi: boolean,
  ): Promise<{ cart: ShoppingCartDto | undefined, validationResult: ShoppingCartValidationResultDto | undefined }> {
    this.logger.trace(`CartDataService cartLatestGetMergedWithCartCurrentAsync called.`, this.constructor.name);
    let cartGetResult: ShoppingCartGetQueryResult = await firstValueFrom(this.cartGet(EShoppingCartGetQueryType.GetLatest, this.shouldValidateCarts));
    let cartNew = cartGetResult.shoppingCart;
    if (cartNew == undefined) {
      return { cart: undefined, validationResult: undefined };
    }

    this.cartCurrent = cartNew;
    return { cart: cartNew, validationResult: cartGetResult.shoppingCartValidationResult };
  }

  private permissionSubscriptionInit() {
    this.authService.userPermissionChangedGet().subscribe(
      () => {
        this.shouldLoadPrices = this.shouldLoadPricesCurrentStateGet();
      }
    );
  }

  protected shouldLoadPricesCurrentStateGet(): boolean | undefined {
    return ENVIRONMENT.price.allowAnonymousPrice || (this.isLoggedIn && this.authService?.canSeePrices());
  }

  private update() {
    if (this.isLoggedIn) {
      this.cartTemplateListInitAsync();
      this.cartCurrentInitAsync(EShoppingCartGetQueryType.GetLatest);
    } else {
      this.cartCurrentReplaceBy(undefined);
      this.cartValidationStateSet(undefined, undefined);
    }
  }

  public async cartCurrentReplaceByApiAsync(cartGetQueryType: EShoppingCartGetQueryType, validateCart?: boolean | undefined) {
    let shouldValidateNewCart = validateCart;
    if (shouldValidateNewCart == undefined) {
      shouldValidateNewCart = this.shouldValidateCarts;
    }
    try {
      let cartGetResult = await firstValueFrom(this.cartGet(cartGetQueryType, shouldValidateNewCart));
      this.cartCurrentReplaceBy(cartGetResult?.shoppingCart);

      if (this.shouldValidateCarts == true) {
        this.cartValidationStateSet(undefined, cartGetResult?.shoppingCartValidationResult);
      }

    } catch (error) {
      this.cartCurrentReplaceBy(undefined);
      this.cartValidationStateSet(undefined, undefined);
      this.logger.error(error, this.constructor.name);
      throw error;
    }
  }

  public cartCurrentReplaceBy(cart: ShoppingCartDto | undefined) {
    let deliveryDate = cart?.deliveryDate ? new Date(cart.deliveryDate) : undefined;
    this.productService.cartDeliveryDate = deliveryDate;

    let deliveryAddress = cart?.deliveryAddressData;
    this.productService.cartDeliveryAddress = deliveryAddress;

    super.cartCurrentReplaceBy(cart);
  }

  public async cartItemListSetAsync(shoppingCart: ShoppingCartDto | undefined, shoppingCartSetItemList: ShoppingCartSetItemDto[]): Promise<ShoppingCartDto | undefined> {
    if (shoppingCart == undefined) {
      shoppingCart = this.cartCurrent;
    }

    let updatedCartIsCurrentCart = this.cartCurrent?.cartNumber == shoppingCart?.cartNumber;

    if (shoppingCart == undefined) {
      throw new Error('No current shoppping cart given upon adding / updating items.');
    }
    if (shoppingCartSetItemList.length <= 0) {
      return undefined;
    }

    if (!shoppingCart.cartNumber || shoppingCart.cartNumber <= 0) {
      throw new Error('Invalid cartNumber.');
    }

    // Prüfen ob das so i.O. ist.
    let deliveryDate = shoppingCart?.deliveryDate != undefined ? shoppingCart.deliveryDate : new Date();
    let validateResult = await firstValueFrom(this.shoppingCartApiService.shoppingCartValidate(shoppingCart.cartNumber)).then((result) => {
      let shoppingCartValidationResult = result.shoppingCartValidationResult;
      let isDeliveryDateInvalid = shoppingCartValidationResult?.cartErrorList?.find(e => e.errorType == EShoppingCartValidationError.DeliveryDateIsInvalid) != undefined;
      if (isDeliveryDateInvalid) {
        return this.cartCreationDialogService.cartCreationDialogShow(
          // component,
          () => {
            return;
          },
          shoppingCart,
          false
        );
      }
      return true;
    });

    if (!validateResult || validateResult === undefined || validateResult === null) {
      return undefined;
    }

    let result = await firstValueFrom(this.shoppingCartApiService.shoppingCartItemSet(shoppingCartSetItemList, shoppingCart.cartNumber, deliveryDate, true)).then((shoppingCartItemSetCommandResult) => {
      let shoppingCartResult = shoppingCartItemSetCommandResult.shoppingCart;
      let shoppingCartValidationResult = shoppingCartItemSetCommandResult.shoppingCartValidationResult;


      if (!shoppingCartResult) {
        return undefined;
      }

      if (updatedCartIsCurrentCart == true) {
        this.cartValidationStateSet(shoppingCartResult, shoppingCartValidationResult);
      }

      return shoppingCartResult;
    }).catch(async (error) => {
      if (error instanceof SwaggerException) {
        let swaggerException = error as SwaggerException;
        let exception = swaggerException.result;

        if (exception instanceof ShoppingCartValidationException) {
          let shoppingCartValidationException = exception as ShoppingCartValidationException;
          let validationResult = shoppingCartValidationException.shoppingCartValidationResult;
          let deleteItemList: ProductIdentifierDto[] = [];

          let hasItemValidationError = validationResult?.itemValidationErrorList != undefined && validationResult?.itemValidationErrorList.length > 0;

          let toastService = ServiceLocator.injector.get(IToastServiceToken);

          if (hasItemValidationError) {

            let message = $localize`:@@CartDataServiceCartItemSetValidationErrorMessage:Cart could not be updated.`;
            message += '<br><br>';

            let stockInformationErrorList = validationResult?.itemValidationErrorList?.filter((e) => e.shoppingCartItemValidationError === EShoppingCartItemValidationError.ProductIsOutOfStock);
            let stockInformationGetList = stockInformationErrorList?.map(
              (e) => {
                let requestDto = new ProductStockInformationGetDto();
                let shoppingCartSetItem = shoppingCartSetItemList.find((s) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(s.shoppingCartItemIdentifier?.productIdentifier, e.productIdentifier));
                requestDto.productIdentifier = shoppingCartSetItem?.shoppingCartItemIdentifier?.productIdentifier;
                requestDto.quantityUnit = shoppingCartSetItem?.shoppingCartItemIdentifier?.orderUnit;
                return requestDto;
              }
            );
            let stockInformationList: ProductStockInformationDto[] | undefined;

            await firstValueFrom(this.productService.productStockInformationGet(stockInformationGetList, deliveryDate)).then((result) => {
              stockInformationList = result.productStockInformationList;
            }).catch((error) => {
              this.logger.warn(error, this.constructor.name);
            });

            for await (let shoppingCartSetItem of shoppingCartSetItemList) {
              let cartItemValidationErrorList = validationResult?.itemValidationErrorList?.filter(
                (e) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(e.productIdentifier, shoppingCartSetItem.shoppingCartItemIdentifier?.productIdentifier)
              );
              if (cartItemValidationErrorList == undefined) { continue; }

              let cartItemValidationErrorCount = cartItemValidationErrorList.length;
              if (cartItemValidationErrorCount <= 0) { continue; }

              message += `Product #${shoppingCartSetItem.shoppingCartItemIdentifier?.productIdentifier?.productNumber}:`;
              message += '<br>';
              for (let itemError of cartItemValidationErrorList) {
                let errorExplanation = CartValidationHelperService.shoppingCartItemValidationErrorExplanationGet(itemError.shoppingCartItemValidationError);
                message += `- ${errorExplanation}<br>`;
                let isProductOutOfStock = itemError.shoppingCartItemValidationError === EShoppingCartItemValidationError.ProductIsOutOfStock;
                if (isProductOutOfStock) {
                  let productStockInformation = stockInformationList?.find((s) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(s.productIdentifier, itemError.productIdentifier));
                  if (productStockInformation == undefined) { continue; }

                  let quantityUnitHasSmallerUnit = productStockInformation.quantityUnit?.consistsOfQuantity != undefined;
                  let stockMinThreshold = ENVIRONMENT.availability.stockMinThreshold ? ENVIRONMENT.availability.stockMinThreshold : 0;
                  let isStockBelowOrEqual0 = productStockInformation.availableQuantity <= stockMinThreshold;
                  let availableQuantityRounded = Math.floor(productStockInformation.availableQuantity * 100) / 100;
                  if (!quantityUnitHasSmallerUnit) {
                    availableQuantityRounded = Math.floor(availableQuantityRounded);
                  }
                  let usePlural = availableQuantityRounded > 1;
                  let pluralString = usePlural ? 's' : '';
                  shoppingCartSetItem.quantity = availableQuantityRounded;
                  if (isStockBelowOrEqual0) {
                    if (shoppingCartSetItem.shoppingCartItemIdentifier?.productIdentifier != undefined) {
                      deleteItemList.push(shoppingCartSetItem.shoppingCartItemIdentifier.productIdentifier);
                      let deleteIndex = shoppingCartSetItemList.indexOf(shoppingCartSetItem);
                      shoppingCartSetItemList.splice(deleteIndex, 1);
                    }

                    let stockAvailableBelow0Prefix = $localize`:@@CartDataServiceCartItemSetStockInformationAvailableBelow0Prefix:There are no products `;
                    let stockAvailableBelow0Suffix = $localize`:@@CartDataServiceCartItemSetStockInformationAvailableBelow0Suffix: in stock.`;
                    message += `${stockAvailableBelow0Prefix}(${productStockInformation.quantityUnit?.quantityUnitCaption}) ${stockAvailableBelow0Suffix}`;
                    message += '<br>';
                  } else {
                    let stockAvailableQuantityPrefix = $localize`:@@CartDataServiceCartItemSetStockInformationAvailableQuantityPrefix:It is possible to order only a quantity of `;
                    let stockAvailableQuantitySuffix = $localize`:@@CartDataServiceCartItemSetStockInformationAvailableQuantitySuffix:.`;
                    message += `${stockAvailableQuantityPrefix}${availableQuantityRounded} (${productStockInformation.quantityUnit?.quantityUnitCaption}${pluralString}) ${stockAvailableQuantitySuffix}`;
                    message += '<br>';
                  }
                }
                message += '<br>';
              }
            }

            if (shoppingCart != undefined) {
              this.cartItemListDeleteAsync(deleteItemList, shoppingCart.cartNumber, true);
            }

            toastService.toastItemAdd(new ToastItem({
              type: EToastItemType.Danger,
              title: $localize`:@@CartDataServiceCartItemSetValidationErrorTitle:Cart update failed`,
              message: message,
              code: JSON.parse(error.response).errorCode
            }));
          }
        }
        this.logger.error(exception, this.constructor.name);
      } else {
        this.logger.error(error, this.constructor.name);
      }
      if (this.isStockError) {
        let newResult = this.cartItemListSetAsync(shoppingCart, shoppingCartSetItemList);
        return newResult;
      }
      return;
    }).finally(() => this.isStockError = false);
    return result;
  }

}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const CartTourServiceProvider: Provider[] = [
  { provide: ICartServiceToken, useExisting: CartTourService }
];
